import React from 'react';
import { useTranslation } from 'react-i18next';

export const WhitepaperPage: React.FC = () => {
    const { t } = useTranslation();

    const Divider = () => (
        <div className="flex justify-center my-16">
            <div className="h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent w-full max-w-2xl"></div>
        </div>
    );

    const s = (key: string, opts?: any): any => t(`pages.whitepaper.sections.${key}` as any, opts);

    return (
        <div className="min-h-screen relative overflow-hidden py-20 bg-bg-primary">
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-900/5 rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10 max-w-4xl">

                {/* Header */}
                <div className="text-center mb-20 animate-fade-in">
                    <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
                        <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.whitepaper.badge')}</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-white mb-6 bg-gradient-to-r from-gold to-white bg-clip-text text-transparent">
                        {t('pages.whitepaper.title')}
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-300 mx-auto leading-relaxed font-light">
                        {t('pages.whitepaper.subtitle')}
                    </p>
                </div>

                {/* 1. Pendahuluan */}
                <div className="bg-surface/50 backdrop-blur-sm border border-gold/10 rounded-3xl p-8 md:p-12 mb-16 shadow-lg">
                    <h2 className="text-3xl font-bold text-white mb-6 border-l-4 border-gold pl-4">{s('intro.title')}</h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed text-lg mb-6">
                        {(s('intro.content', { returnObjects: true }) as unknown as string[]).map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                    <div className="bg-gold/10 border border-gold/30 rounded-xl p-5">
                        <p className="text-gold font-medium italic">{s('intro.highlight')}</p>
                    </div>
                </div>

                <Divider />

                {/* 2. Visi dan Misi */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-8">{s('vision.title')}</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-surface p-6 rounded-xl border border-gold/20">
                            <h3 className="text-xl font-bold text-gold mb-3">{s('vision.visionTitle')}</h3>
                            <p className="text-gray-300 leading-relaxed">{s('vision.visionContent')}</p>
                        </div>
                        <div className="bg-surface p-6 rounded-xl border border-gold/10">
                            <h3 className="text-xl font-bold text-white mb-4">{s('vision.missionTitle')}</h3>
                            <ul className="space-y-3">
                                {(s('vision.missionItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-gray-300">
                                        <span className="text-gold mt-1">•</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* 3. Konsep Dasar */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{s('concept.title')}</h2>
                    <p className="text-gray-300 mb-4 text-lg">{s('concept.intro')}</p>
                    <ul className="space-y-3 mb-6">
                        {(s('concept.items', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-gray-200 bg-surface/50 p-4 rounded-lg border border-gray-800">
                                <span className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0"></span>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-gray-400 italic border-l-2 border-gold/40 pl-4">{s('concept.conclusion')}</p>
                </div>

                <Divider />

                {/* 5. Ecosystem Value Mechanism */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{s('redemption.title')}</h2>
                    <p className="text-gray-300 mb-6 text-lg">{s('redemption.intro')}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-surface p-6 rounded-xl border border-gray-800">
                            <p className="text-gold font-semibold mb-4">{s('redemption.flowTitle')}</p>
                            <ol className="space-y-3">
                                {(s('redemption.flowItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-gray-300">
                                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gold/20 border border-gold/50 flex items-center justify-center text-gold text-sm font-bold">{i + 1}</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <div className="bg-surface p-6 rounded-xl border border-gray-800">
                            <p className="text-gold font-semibold mb-4">{s('redemption.conditionTitle')}</p>
                            <ul className="space-y-3">
                                {(s('redemption.conditionItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-gray-300">
                                        <i className="fas fa-info-circle text-gold/50"></i>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <Divider />

                {/* 6. Ekosistem Utility */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-4">{s('ecosystem.title')}</h2>
                    <p className="text-gray-300 mb-8 text-lg">{s('ecosystem.intro')}</p>
                    <div className="space-y-6">
                        {(s('ecosystem.subsections', { returnObjects: true }) as unknown as any[]).map((sub, i) => (
                            <div key={i} className="bg-surface p-6 rounded-xl border border-gray-800 hover:border-gold/20 transition-colors">
                                <h3 className="text-xl font-bold text-white mb-3">{sub.title}</h3>
                                {sub.intro && <p className="text-gray-400 mb-2">{sub.intro}</p>}
                                {sub.content && <p className="text-gray-300 mb-4">{sub.content}</p>}
                                <ul className="space-y-2 mb-3">
                                    {sub.items.map((item: string, j: number) => (
                                        <li key={j} className="flex items-center gap-3 text-gray-300">
                                            <span className="w-2 h-2 rounded-full bg-gold flex-shrink-0"></span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                {sub.impactItems && sub.impactItems.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-700">
                                        <p className="text-sm text-gray-400 mb-2">Dampak / Impact:</p>
                                        <ul className="flex flex-wrap gap-2">
                                            {sub.impactItems.map((item: string, j: number) => (
                                                <li key={j} className="bg-green-900/20 text-green-400 text-sm px-3 py-1 rounded-full border border-green-900/30">{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* 7. Demand Model */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{s('demand.title')}</h2>
                    <p className="text-gray-300 mb-6 text-lg">{s('demand.intro')}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-surface p-6 rounded-xl border border-gray-800">
                            <p className="text-white font-semibold mb-4">{s('demand.sourceTitle')}</p>
                            <ul className="grid grid-cols-2 gap-3">
                                {(s('demand.sourceItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="bg-surface/80 p-3 rounded-lg border border-gray-700 text-center text-gray-300 text-sm">{item}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-gradient-to-br from-gold/10 to-surface p-6 rounded-xl border border-gold/20 flex flex-col justify-center">
                            <p className="text-gold font-semibold mb-4">{s('demand.loopTitle')}</p>
                            <p className="text-white font-bold text-lg leading-relaxed">{s('demand.loop')}</p>
                        </div>
                    </div>
                </div>

                <Divider />

                {/* 8. Tokenomics */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{s('tokenomics.title')}</h2>
                    <p className="text-gray-300 mb-8 text-lg">{s('tokenomics.intro')}</p>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-surface p-6 rounded-xl border border-gray-800">
                            <p className="text-gold font-semibold mb-4">{s('tokenomics.principleTitle')}</p>
                            <ul className="space-y-2">
                                {(s('tokenomics.principleItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="text-gray-300 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gold"></span>{item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-surface p-6 rounded-xl border border-gray-800">
                            <p className="text-gold font-semibold mb-4">{s('tokenomics.mechanismTitle')}</p>
                            <ul className="space-y-2">
                                {(s('tokenomics.mechanismItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="text-gray-300 flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0"></span>{item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-surface p-6 rounded-xl border border-green-900/30">
                            <p className="text-green-400 font-semibold mb-4">{s('tokenomics.resultTitle')}</p>
                            <ul className="space-y-2">
                                {(s('tokenomics.resultItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="text-gray-300 flex items-center gap-2">
                                        <span className="text-green-400">✓</span>{item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* 9. Keunggulan Kompetitif */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-8">{s('competitive.title')}</h2>
                    <div className="overflow-x-auto rounded-xl border border-gray-800">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-surface/80">
                                    {(s('competitive.tableHeaders', { returnObjects: true }) as unknown as string[]).map((h, i) => (
                                        <th key={i} className={`p-4 text-left font-semibold ${i === 3 ? 'text-gold' : 'text-gray-300'}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(s('competitive.tableRows', { returnObjects: true }) as unknown as any[]).map((row, i) => (
                                    <tr key={i} className="border-t border-gray-800 hover:bg-surface/30 transition-colors">
                                        <td className="p-4 text-gray-300 font-medium">{row.aspect}</td>
                                        <td className="p-4 text-center text-lg">{row.traditional}</td>
                                        <td className="p-4 text-center text-lg">{row.other}</td>
                                        <td className="p-4 text-center text-lg bg-gold/5">{row.idng}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-gold font-semibold text-center mt-6 bg-gold/10 p-4 rounded-xl border border-gold/20">{s('competitive.conclusion')}</p>
                </div>

                {/* 10. Roadmap */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-8">{s('roadmap.title')}</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {(s('roadmap.phases', { returnObjects: true }) as unknown as any[]).map((phase, i) => (
                            <div key={i} className="flex gap-4 bg-surface p-6 rounded-xl border border-gray-800 hover:border-gold/30 transition-colors">
                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold/20 border border-gold flex items-center justify-center text-gold font-bold">
                                    {i + 1}
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white mb-3">{phase.title}</h3>
                                    <ul className="space-y-2">
                                        {phase.items.map((item: string, j: number) => (
                                            <li key={j} className="flex items-center gap-2 text-sm text-gray-400">
                                                <span className="text-gold">•</span>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Divider />

                {/* 11. Penutup */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-3xl font-bold text-white mb-6">{s('conclusion.title')}</h2>
                    <p className="text-gray-300 text-lg mb-8">{s('conclusion.intro')}</p>
                    <div className="grid md:grid-cols-2 gap-6 text-left mb-8">
                        <div className="bg-surface p-6 rounded-xl border border-gray-800">
                            <p className="text-gold font-semibold mb-4">{s('conclusion.combiningTitle')}</p>
                            <ul className="space-y-3">
                                {(s('conclusion.combiningItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-gray-300">
                                        <span className="text-gold">+</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-gradient-to-br from-gold/10 to-surface p-6 rounded-xl border border-gold/20">
                            <p className="text-gold font-semibold mb-4">{s('conclusion.resultTitle')}</p>
                            <ul className="space-y-3">
                                {(s('conclusion.resultItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                                    <li key={i} className="text-white font-medium text-lg leading-relaxed">"{item}"</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-10 pt-10 border-t border-gray-800 text-center text-gray-500 text-sm">
                    <p>{t('pages.whitepaper.footer')}</p>
                </div>

            </div>
        </div>
    );
};
